import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactSelect from "react-select";
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Input,
  InputNumber,
  Select,
  Switch,
  Tabs,
  Tag,
} from "antd";
import SidebarNav from "../../sidebar";
import {
  addCoinNetwork,
  getCoinList,
  getCoinNetworkById,
  updateCoinNetwork,
  getFireblocksEnv,
  getFireblocksSupportedAssets,
  postFireblocksSyncMapping,
} from "../../../../service/api";
import PermissionGate from "../../commoncomponent/PermissionGate";

const CHAIN_TYPES = [
  { value: "ACCOUNT_BASED", label: "Account-based (ETH, TRX, SOL)" },
  { value: "UTXO", label: "UTXO (BTC, LTC)" },
  { value: "TAG_MEMO", label: "Tag / memo (XRP, XLM)" },
];

const emptyFireblocksAsset = () => ({
  assetId: "",
  assetName: "",
  nativeAsset: "",
  decimals: 6,
  contractAddress: "",
  blockchain: "",
  sharedVaultId: "",
  sweepMinAmount: null,
});

const emptyNetwork = () => ({
  networkKey: "",
  chainName: "",
  chainType: "ACCOUNT_BASED",
  address: "",
  networkIcon: null,
  explorerTxUrlTemplate: null,
  explorerAddressUrlTemplate: null,
  isDepositEnable: false,
  isWithdrawEnable: false,
  fireblocksAssets: { sandbox: null, production: null },
  minDeposit: 0,
  minWithdraw: 0,
  maxWithdrawPerTx: 0,
  maxWithdrawPerDay: 0,
  withdrawFeeFlat: 0,
  withdrawFeePct: 0,
  networkFeeBuffer: 0,
  confirmationsRequired: 1,
  addressRegex: null,
  memoRequired: false,
});

const normaliseDoc = (raw) => {
  // Existing backend sometimes returns the doc itself, sometimes wrapped in
  // `result[0]` — handle both. The legacy shape had a single flat network
  // stored on the top-level object, so lift that into networks[] if present.
  if (!raw) return { coin: "", networks: [] };
  const doc = raw?.result?.[0] || raw;
  if (Array.isArray(doc.networks)) {
    return {
      _id: doc._id,
      coin:
        typeof doc.coin === "object" ? doc.coin?._id || doc.coin : doc.coin,
      networks: doc.networks.map((n) => ({ ...emptyNetwork(), ...n })),
    };
  }
  if (doc.chainName || doc.chainType) {
    return {
      _id: doc._id,
      coin:
        typeof doc.coin === "object" ? doc.coin?._id || doc.coin : doc.coin,
      networks: [
        {
          ...emptyNetwork(),
          networkKey: doc.chainName?.toLowerCase() || "",
          chainName: doc.chainName || "",
          chainType: doc.chainType || "ACCOUNT_BASED",
          address: doc.chainAddress || "",
          fireblocksAssets: {
            sandbox: doc.assetsName
              ? { ...emptyFireblocksAsset(), assetId: doc.assetsName }
              : null,
            production: null,
          },
        },
      ],
    };
  }
  return {
    _id: doc?._id,
    coin: doc?.coin || "",
    networks: [],
  };
};

const CoinNetworkForm = () => {
  const { id } = useParams();
  const history = useHistory();

  const [coinOptions, setCoinOptions] = useState([]);
  const [doc, setDoc] = useState({ coin: "", networks: [] });
  const [loading, setLoading] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const [envLoading, setEnvLoading] = useState(true);

  // Drawer for the Fireblocks supported-assets picker.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFor, setPickerFor] = useState({ networkIndex: -1, env: "sandbox" });
  const [pickerAssets, setPickerAssets] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const env = await getFireblocksEnv();
        if (env?.status) setEnvInfo(env.data);
      } catch {
        /* env info is a nice-to-have; page still works */
      } finally {
        setEnvLoading(false);
      }
    })();
    getCoinList({ page: 1, sizePerPage: 10, pagination: false }).then((res) => {
      if (res?.status) {
        setCoinOptions(
          (res?.data?.result || []).map((c) => ({
            label: `${c.symbol || c.name} — ${c.name}`,
            value: c._id,
          }))
        );
      }
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    getCoinNetworkById(id)
      .then((res) => setDoc(normaliseDoc(res?.data)))
      .catch(() => toast.error("Failed to load coin network", { toastId: 'coin-network-form-load-error' }));
  }, [id]);

  const setNetwork = (index, updater) => {
    setDoc((d) => {
      const next = { ...d, networks: [...(d.networks || [])] };
      const current = next.networks[index] || emptyNetwork();
      next.networks[index] =
        typeof updater === "function" ? updater(current) : { ...current, ...updater };
      return next;
    });
  };

  const setFireblocksAsset = (index, env, patch) => {
    setNetwork(index, (n) => ({
      ...n,
      fireblocksAssets: {
        ...(n.fireblocksAssets || { sandbox: null, production: null }),
        [env]: { ...(n.fireblocksAssets?.[env] || emptyFireblocksAsset()), ...patch },
      },
    }));
  };

  const addNetwork = () =>
    setDoc((d) => ({ ...d, networks: [...(d.networks || []), emptyNetwork()] }));

  const removeNetwork = (index) =>
    setDoc((d) => ({
      ...d,
      networks: (d.networks || []).filter((_, i) => i !== index),
    }));

  const validate = () => {
    if (!doc.coin) {
      toast.error("Pick a coin", { toastId: 'coin-network-form-validation-error' });
      return false;
    }
    if (!doc.networks?.length) {
      toast.error("Add at least one network", { toastId: 'coin-network-form-validation-error' });
      return false;
    }
    const seen = new Set();
    for (const [i, n] of doc.networks.entries()) {
      if (!n.networkKey) {
        toast.error(`Network ${i + 1}: networkKey is required`, { toastId: 'coin-network-form-validation-error' });
        return false;
      }
      if (seen.has(n.networkKey)) {
        toast.error(`Duplicate networkKey "${n.networkKey}"`, { toastId: 'coin-network-form-validation-error' });
        return false;
      }
      seen.add(n.networkKey);
      if (!n.chainName) {
        toast.error(`Network ${i + 1}: chainName is required`, { toastId: 'coin-network-form-validation-error' });
        return false;
      }
      if (!n.chainType) {
        toast.error(`Network ${i + 1}: chainType is required`, { toastId: 'coin-network-form-validation-error' });
        return false;
      }
      if (n.isDepositEnable || n.isWithdrawEnable) {
        const activeEnv = envInfo?.env || "sandbox";
        const fb = n.fireblocksAssets?.[activeEnv];
        if (!fb?.assetId) {
          toast.error(
            `Network "${n.chainName}" is enabled but has no Fireblocks asset mapped for ${activeEnv}.`,
            { toastId: 'coin-network-form-validation-error' }
          );
          return false;
        }
      }
      if (
        Number(n.minWithdraw) > Number(n.maxWithdrawPerTx || 0) &&
        n.isWithdrawEnable
      ) {
        toast.error(
          `Network "${n.chainName}": minWithdraw must be ≤ maxWithdrawPerTx`,
          { toastId: 'coin-network-form-validation-error' }
        );
        return false;
      }
      if (Number(n.withdrawFeePct) < 0 || Number(n.withdrawFeePct) > 100) {
        toast.error(`Network "${n.chainName}": withdrawFeePct must be 0-100`, { toastId: 'coin-network-form-validation-error' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const payload = { coin: doc.coin, networks: doc.networks };
    // api.js signature is updateCoinNetwork(data, id) — keep that order or
    // the id ends up in the body and the body ends up in the URL path.
    const call = id ? updateCoinNetwork(payload, id) : addCoinNetwork(payload);
    call
      .then((res) => {
        if (res?.status === false) {
          toast.error(res?.message || "Save failed", { toastId: 'coin-network-form-save-error' });
          return;
        }
        toast.success(res?.message || "Saved");
        history.push("/admin/network");
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || "Save failed", { toastId: 'coin-network-form-save-error' })
      )
      .finally(() => setLoading(false));
  };

  const openPicker = async (networkIndex, env) => {
    setPickerFor({ networkIndex, env });
    setPickerOpen(true);
    if (pickerAssets.length === 0) {
      setPickerLoading(true);
      try {
        const res = await getFireblocksSupportedAssets();
        if (res?.status) {
          setPickerAssets([
            ...(res.data?.mapped || []),
            ...(res.data?.unmapped || []),
          ]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Fireblocks load failed");
      } finally {
        setPickerLoading(false);
      }
    }
  };

  const pickAsset = async (asset) => {
    const { networkIndex, env } = pickerFor;
    if (networkIndex < 0) return;
    setFireblocksAsset(networkIndex, env, {
      assetId: asset.assetId,
      assetName: asset.name,
      nativeAsset: asset.nativeAsset,
      decimals: asset.decimals,
      contractAddress: asset.contractAddress || "",
      blockchain: asset.blockchain || "",
    });
    // Persist through the sync-mapping endpoint so the wallet-backend cache
    // refreshes even if the admin forgets to save the form below.
    if (doc?._id && doc.networks[networkIndex]?.networkKey) {
      try {
        await postFireblocksSyncMapping({
          coinId: doc.coin,
          networkKey: doc.networks[networkIndex].networkKey,
          env,
          assetId: asset.assetId,
        });
        toast.success(`Mapped ${asset.assetId} for ${env}`);
      } catch (e) {
        // silent — admin can Save to apply
      }
    }
    setPickerOpen(false);
  };

  const filteredPickerAssets = useMemo(() => {
    if (!pickerSearch) return pickerAssets;
    const q = pickerSearch.toLowerCase();
    return pickerAssets.filter(
      (a) =>
        (a.assetId || "").toLowerCase().includes(q) ||
        (a.name || "").toLowerCase().includes(q) ||
        (a.nativeAsset || "").toLowerCase().includes(q)
    );
  }, [pickerAssets, pickerSearch]);

  const activeEnv = envInfo?.env || "sandbox";

  const renderFireblocksTab = (net, index, env) => {
    const fb = net.fireblocksAssets?.[env] || emptyFireblocksAsset();
    const isActiveEnv = env === activeEnv;
    return (
      <div>
        <div style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <PermissionGate module="fireblocks-config" action="update">
            <Button onClick={() => openPicker(index, env)} type="primary" size="small">
              Pick from Fireblocks Sync
            </Button>
          </PermissionGate>
          {!isActiveEnv && (
            <Tag color="default">
              Backend active env is {activeEnv.toUpperCase()} — {env} mapping
              is stored but not loaded into the registry until you deploy there.
            </Tag>
          )}
        </div>
        <div className="row">
          <div className="col-md-6">
            <label>Fireblocks asset ID</label>
            <Input
              value={fb.assetId}
              onChange={(e) =>
                setFireblocksAsset(index, env, { assetId: e.target.value })
              }
              placeholder="USDT_ERC20"
            />
          </div>
          <div className="col-md-6">
            <label>Asset name</label>
            <Input
              value={fb.assetName}
              onChange={(e) =>
                setFireblocksAsset(index, env, { assetName: e.target.value })
              }
            />
          </div>
          <div className="col-md-4">
            <label>Native asset</label>
            <Input
              value={fb.nativeAsset}
              onChange={(e) =>
                setFireblocksAsset(index, env, { nativeAsset: e.target.value })
              }
            />
          </div>
          <div className="col-md-4">
            <label>Decimals</label>
            <InputNumber
              value={fb.decimals}
              min={0}
              max={36}
              style={{ width: "100%" }}
              onChange={(v) =>
                setFireblocksAsset(index, env, { decimals: v ?? 0 })
              }
            />
          </div>
          <div className="col-md-4">
            <label>Blockchain</label>
            <Input
              value={fb.blockchain}
              onChange={(e) =>
                setFireblocksAsset(index, env, { blockchain: e.target.value })
              }
            />
          </div>
          <div className="col-md-8">
            <label>Contract address</label>
            <Input
              value={fb.contractAddress}
              onChange={(e) =>
                setFireblocksAsset(index, env, {
                  contractAddress: e.target.value,
                })
              }
              placeholder="0x…"
            />
          </div>
          <div className="col-md-4">
            <label>Shared vault id (UTXO / tag)</label>
            <Input
              value={fb.sharedVaultId || ""}
              onChange={(e) =>
                setFireblocksAsset(index, env, { sharedVaultId: e.target.value })
              }
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ paddingBottom: 60 }}>
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">
                  {id ? "Edit" : "Add"} Coin Network
                </h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/network">Coin Network</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    {id ? "Edit" : "Add"} Coin Network
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {!envLoading && envInfo && (
            <Alert
              type={envInfo.env === "production" ? "error" : "warning"}
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <span>
                  Wallet backend env: <b>{envInfo.env.toUpperCase()}</b>. The
                  per-env <code>fireblocksAssets.{envInfo.env}</code> block is
                  the one users will see.
                </span>
              }
            />
          )}

          <form onSubmit={handleSubmit}>
            <div className="card">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <label>
                      Coin <span className="text-danger">*</span>
                    </label>
                    <ReactSelect
                      options={coinOptions}
                      value={coinOptions.find((o) => o.value === doc.coin)}
                      onChange={(e) => setDoc({ ...doc, coin: e.value })}
                      isDisabled={!!id}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h5 style={{ margin: 0 }}>Networks</h5>
              <Button type="primary" onClick={addNetwork}>
                + Add network
              </Button>
            </div>

            {doc.networks.length === 0 && (
              <Alert type="info" showIcon message="No networks yet. Click “Add network” to configure USDT on ERC-20, TRC-20, BSC etc." />
            )}

            <Collapse
              defaultActiveKey={doc.networks.map((_, i) => String(i))}
              items={doc.networks.map((net, index) => ({
                key: String(index),
                label: (
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <b>{net.chainName || `Network ${index + 1}`}</b>
                    {net.networkKey && <Tag>{net.networkKey}</Tag>}
                    <Tag color={net.isDepositEnable ? "green" : "red"}>
                      Dep {net.isDepositEnable ? "ON" : "OFF"}
                    </Tag>
                    <Tag color={net.isWithdrawEnable ? "green" : "red"}>
                      Wdr {net.isWithdrawEnable ? "ON" : "OFF"}
                    </Tag>
                  </div>
                ),
                extra: (
                  <Button
                    size="small"
                    danger
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNetwork(index);
                    }}
                  >
                    Remove
                  </Button>
                ),
                children: (
                  <div>
                    <div className="row">
                      <div className="col-md-3">
                        <label>Network key *</label>
                        <Input
                          value={net.networkKey}
                          onChange={(e) =>
                            setNetwork(index, {
                              networkKey: e.target.value.toLowerCase(),
                            })
                          }
                          placeholder="ethereum"
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Display chain name *</label>
                        <Input
                          value={net.chainName}
                          onChange={(e) => setNetwork(index, { chainName: e.target.value })}
                          placeholder="Ethereum (ERC-20)"
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Chain type</label>
                        <Select
                          style={{ width: "100%" }}
                          value={net.chainType}
                          onChange={(v) => setNetwork(index, { chainType: v })}
                          options={CHAIN_TYPES}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Confirmations</label>
                        <InputNumber
                          value={net.confirmationsRequired}
                          min={1}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { confirmationsRequired: v ?? 1 })
                          }
                        />
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: 12 }}>
                      <div className="col-md-3">
                        <label>Deposit enabled</label>
                        <div>
                          <Switch
                            checked={!!net.isDepositEnable}
                            onChange={(v) => setNetwork(index, { isDepositEnable: v })}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label>Withdraw enabled</label>
                        <div>
                          <Switch
                            checked={!!net.isWithdrawEnable}
                            onChange={(v) => setNetwork(index, { isWithdrawEnable: v })}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label>Memo required</label>
                        <div>
                          <Switch
                            checked={!!net.memoRequired}
                            onChange={(v) => setNetwork(index, { memoRequired: v })}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label>Network fee buffer</label>
                        <InputNumber
                          value={net.networkFeeBuffer}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { networkFeeBuffer: v ?? 0 })
                          }
                        />
                      </div>
                    </div>

                    <h6 style={{ marginTop: 16 }}>Limits &amp; fees</h6>
                    <div className="row">
                      <div className="col-md-3">
                        <label>Min deposit</label>
                        <InputNumber
                          value={net.minDeposit}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) => setNetwork(index, { minDeposit: v ?? 0 })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Min withdraw</label>
                        <InputNumber
                          value={net.minWithdraw}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) => setNetwork(index, { minWithdraw: v ?? 0 })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Max withdraw / tx</label>
                        <InputNumber
                          value={net.maxWithdrawPerTx}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { maxWithdrawPerTx: v ?? 0 })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Max withdraw / day</label>
                        <InputNumber
                          value={net.maxWithdrawPerDay}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { maxWithdrawPerDay: v ?? 0 })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Withdraw fee (flat)</label>
                        <InputNumber
                          value={net.withdrawFeeFlat}
                          min={0}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { withdrawFeeFlat: v ?? 0 })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Withdraw fee (%)</label>
                        <InputNumber
                          value={net.withdrawFeePct}
                          min={0}
                          max={100}
                          style={{ width: "100%" }}
                          onChange={(v) =>
                            setNetwork(index, { withdrawFeePct: v ?? 0 })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Address regex (optional)</label>
                        <Input
                          value={net.addressRegex || ""}
                          onChange={(e) =>
                            setNetwork(index, {
                              addressRegex: e.target.value || null,
                            })
                          }
                          placeholder="^0x[a-fA-F0-9]{40}$"
                        />
                      </div>
                    </div>

                    <h6 style={{ marginTop: 16 }}>Fireblocks mapping</h6>
                    <Tabs
                      type="card"
                      items={[
                        {
                          key: "sandbox",
                          label: "Sandbox",
                          children: renderFireblocksTab(net, index, "sandbox"),
                        },
                        {
                          key: "production",
                          label: "Production",
                          children: renderFireblocksTab(net, index, "production"),
                        },
                      ]}
                    />
                  </div>
                ),
              }))}
            />

            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                {id ? "Save changes" : "Create coin network"}
              </Button>
              <Button onClick={() => history.push("/admin/network")}>Cancel</Button>
            </div>
          </form>
        </div>
      </div>

      <Drawer
        title={`Pick Fireblocks asset — ${pickerFor.env.toUpperCase()}`}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        width={720}
      >
        <Input.Search
          placeholder="Search assetId / name / native"
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          style={{ marginBottom: 12 }}
          allowClear
        />
        {pickerLoading && <div>Loading…</div>}
        <div style={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
          {filteredPickerAssets.map((a) => (
            <div
              key={a.assetId}
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <b>{a.assetId}</b>{" "}
                <span style={{ opacity: 0.7, fontSize: 12 }}>
                  {a.name} / {a.nativeAsset} / dec {a.decimals}
                </span>
                {a.contractAddress && (
                  <div style={{ fontSize: 11, opacity: 0.6 }}>
                    {a.contractAddress}
                  </div>
                )}
              </div>
              <Button size="small" type="primary" onClick={() => pickAsset(a)}>
                Use
              </Button>
            </div>
          ))}
          {!pickerLoading && filteredPickerAssets.length === 0 && (
            <div style={{ opacity: 0.6 }}>
              No assets loaded yet. Open the Fireblocks Sync page once so the
              supported-assets list populates.
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default CoinNetworkForm;
